/**
 * localStorage utility functions for MCQ exam answers backup
 * Storage key format: mcq-answers-${userId}-${missionId}
 */

/**
 * Generate storage key for MCQ answers
 * @param {string|number} userId - User ID or username
 * @param {string|number} missionId - Mission ID
 * @returns {string} Storage key
 */
export const getStorageKey = (userId, missionId) => {
  if (!userId || !missionId) {
    console.warn("Missing userId or missionId for storage key");
    return `mcq-answers-${missionId}`;
  }
  return `mcq-answers-${userId}-${missionId}`;
};

/**
 * Load answers from localStorage
 * @param {string|number} userId - User ID or username
 * @param {string|number} missionId - Mission ID
 * @returns {object} Answers object { questionId: answerSymbol }
 */
export const loadAnswersFromStorage = (userId, missionId) => {
  try {
    const userIdStr = String(userId || "");
    const missionIdStr = String(missionId || "");

    if (
      !userIdStr ||
      userIdStr === "null" ||
      !missionIdStr ||
      missionIdStr === "null"
    ) {
      console.warn("loadAnswersFromStorage: missing userId or missionId", {
        userId: userIdStr,
        missionId: missionIdStr,
      });
      return {};
    }

    const key = getStorageKey(userIdStr, missionIdStr);
    const stored = localStorage.getItem(key);

    if (!stored) {
      return {};
    }

    const data = JSON.parse(stored);

    // Return only answer data, exclude metadata
    const { _flagged, _lastSaved, ...answers } = data;
    const cleanAnswers = answers || {};

    // Normalize keys - ensure both string and number versions exist for compatibility
    const normalizedAnswers = {};
    Object.keys(cleanAnswers).forEach((key) => {
      const numKey = Number(key);
      const value = cleanAnswers[key];
      // Store with both string and number keys
      normalizedAnswers[key] = value;
      if (!isNaN(numKey) && String(numKey) === key) {
        normalizedAnswers[numKey] = value;
      }
    });

    return normalizedAnswers;
  } catch (error) {
    console.error("Error loading answers from localStorage:", error);
    return {};
  }
};

/**
 * Save answer to localStorage
 * @param {string|number} userId - User ID or username
 * @param {string|number} missionId - Mission ID
 * @param {string|number} questionId - Question ID or index
 * @param {string} answerSymbol - Answer symbol (A, B, C, D) or empty string
 */
export const saveAnswerToStorage = (
  userId,
  missionId,
  questionId,
  answerSymbol
) => {
  try {
    // Convert to string to handle edge cases
    const userIdStr = String(userId || "");
    const missionIdStr = String(missionId || "");

    if (
      !userIdStr ||
      userIdStr === "null" ||
      !missionIdStr ||
      missionIdStr === "null"
    ) {
      console.warn("Cannot save to localStorage: missing userId or missionId", {
        userId: userIdStr,
        missionId: missionIdStr,
        questionId,
        answerSymbol,
      });
      return;
    }

    const key = getStorageKey(userIdStr, missionIdStr);
    const existing = loadAnswersFromStorage(userIdStr, missionIdStr);

    // Get metadata if exists
    const fullData = JSON.parse(localStorage.getItem(key) || "{}");
    const flagged = fullData._flagged || [];

    // Update answer (even if answerSymbol is empty string, we still save it)
    const updated = {
      ...existing,
      [questionId]: answerSymbol || "",
      _flagged: flagged,
      _lastSaved: new Date().toISOString(),
    };

    localStorage.setItem(key, JSON.stringify(updated));

    // Verify it was saved
    const verify = localStorage.getItem(key);
    if (verify) {
      console.log("✅ Verified localStorage save:", JSON.parse(verify));
    } else {
      console.error("❌ Failed to verify localStorage save");
    }
  } catch (error) {
    console.error("Error saving answer to localStorage:", error, {
      userId,
      missionId,
      questionId,
      answerSymbol,
    });
  }
};

/**
 * Save multiple answers at once
 * @param {string|number} userId - User ID or username
 * @param {string|number} missionId - Mission ID
 * @param {object} answers - Answers object { questionId: answerSymbol }
 */
export const saveAllAnswersToStorage = (userId, missionId, answers) => {
  try {
    const key = getStorageKey(userId, missionId);

    // Get existing metadata
    const existing = JSON.parse(localStorage.getItem(key) || "{}");
    const flagged = existing._flagged || [];

    const updated = {
      ...answers,
      _flagged: flagged,
      _lastSaved: new Date().toISOString(),
    };

    localStorage.setItem(key, JSON.stringify(updated));
  } catch (error) {
    console.error("Error saving all answers to localStorage:", error);
  }
};

/**
 * Remove answer from localStorage
 * @param {string|number} userId - User ID or username
 * @param {string|number} missionId - Mission ID
 * @param {string|number} questionId - Question ID or index
 */
export const removeAnswerFromStorage = (userId, missionId, questionId) => {
  try {
    const key = getStorageKey(userId, missionId);
    const existing = loadAnswersFromStorage(userId, missionId);

    // Get metadata
    const fullData = JSON.parse(localStorage.getItem(key) || "{}");
    const flagged = fullData._flagged || [];

    // Remove answer
    const updated = { ...existing };
    delete updated[questionId];

    const final = {
      ...updated,
      _flagged: flagged,
      _lastSaved: new Date().toISOString(),
    };

    localStorage.setItem(key, JSON.stringify(final));
  } catch (error) {
    console.error("Error removing answer from localStorage:", error);
  }
};

/**
 * Save flagged questions to localStorage
 * @param {string|number} userId - User ID or username
 * @param {string|number} missionId - Mission ID
 * @param {Set|Array} flaggedQuestions - Set or array of flagged question IDs
 */
export const saveFlaggedQuestions = (userId, missionId, flaggedQuestions) => {
  try {
    const key = getStorageKey(userId, missionId);
    const existing = loadAnswersFromStorage(userId, missionId);

    const flaggedArray = Array.isArray(flaggedQuestions)
      ? flaggedQuestions
      : Array.from(flaggedQuestions);

    const updated = {
      ...existing,
      _flagged: flaggedArray,
      _lastSaved: new Date().toISOString(),
    };

    localStorage.setItem(key, JSON.stringify(updated));
  } catch (error) {
    console.error("Error saving flagged questions to localStorage:", error);
  }
};

/**
 * Load flagged questions from localStorage
 * @param {string|number} userId - User ID or username
 * @param {string|number} missionId - Mission ID
 * @returns {Set} Set of flagged question IDs
 */
export const loadFlaggedQuestions = (userId, missionId) => {
  try {
    const key = getStorageKey(userId, missionId);
    const stored = localStorage.getItem(key);
    if (!stored) return new Set();

    const data = JSON.parse(stored);
    const flagged = data._flagged || [];
    return new Set(flagged);
  } catch (error) {
    console.error("Error loading flagged questions from localStorage:", error);
    return new Set();
  }
};

/**
 * Clear all storage for a mission
 * @param {string|number} userId - User ID or username
 * @param {string|number} missionId - Mission ID
 */
export const clearStorage = (userId, missionId) => {
  try {
    const key = getStorageKey(userId, missionId);
    localStorage.removeItem(key);
  } catch (error) {
    console.error("Error clearing localStorage:", error);
  }
};

/**
 * Get user identifier from contextData
 * @param {object} contextData - Context data with user object
 * @returns {string|number|null} User ID or username
 */
export const getUserId = (contextData) => {
  if (!contextData?.user) {
    console.warn("getUserId: No user in contextData", contextData);
    // Fallback: use sessionStorage or a temporary identifier
    return sessionStorage.getItem("tempUserId") || "anonymous";
  }

  // Handle case where user might be an empty string
  if (typeof contextData.user === "string" && contextData.user === "") {
    console.warn("getUserId: user is empty string");
    return sessionStorage.getItem("tempUserId") || "anonymous";
  }

  // Try different possible user identifier fields
  const userId =
    contextData.user.id ||
    contextData.user.userId ||
    contextData.user.username ||
    contextData.user.email ||
    null;

  if (!userId) {
    console.warn(
      "getUserId: Could not extract userId from user object",
      contextData.user
    );
    // Fallback: use sessionStorage or a temporary identifier
    const fallbackId =
      sessionStorage.getItem("tempUserId") || `temp-${Date.now()}`;
    sessionStorage.setItem("tempUserId", fallbackId);
    return fallbackId;
  }

  return userId;
};
